import React, { createElement as _createElement } from 'react';
import Box from '../Box';
import Divider from '../Divider';
import Spacer from '../Spacer';

/**
 * @typedef {import('react').ReactChildren} ReactChildren
 * @typedef {import('react').ReactElement} ReactElement
 * @typedef {import('../utils/props/spacingProps.js').SpacingValue} SpacingValue
 */

/**
 * Takes valid React Children and inserts a specified amount of space between each valid (not nullish) top
 * level element, according to the provided `options`.
 *
 * Returns an array of the original elements plus inserted spacing elements.
 * This array of elements is keyed and may be used as a component's `children` without mapping.
 *
 * @param {ReactChildren} children
 * @param {object} [options] -
 *  - `space`: amount of space to insert using the theme spacing scale (see `useSpacingScale`)
 *  - `direction`: if 'row' or 'row-reverse', applies space horizontally, if 'column' or 'column-reverse', applies space vertically
 *  - `box`: if truthy, wraps each valid child in a Box component; if an object, passes it to Box as props
 *  - `divider`: if truthy, inserts Divider components; if an object, passes it to Divider as props
 *  - `preserveFragments`: if true, adds no space between top-level elements that were passed inside a nested fragment
 * @param {SpacingValue} [options.space]
 * @param {boolean | object} [options.divider]
 * @param {"row" | "column" | "row-reverse" | "column-reverse"} [options.direction]
 * @param {boolean} [options.preserveFragments]
 * @returns {ReactElement[]}
 */
import { jsx as _jsx } from "react/jsx-runtime";
const getStackedContent = (children, _ref) => {
  let {
    divider,
    space,
    direction = 'column',
    box,
    preserveFragments = false
  } = _ref;
  const boxProps = box && typeof box === 'object' ? box : {
    space
  };
  const dividerProps = divider && typeof divider === 'object' ? divider : {};

  // Avoid surprises if children contains comments, nulls, or is a variable wrapped as a fragment
  const topLevelChildren = preserveFragments ? children : unpackFragment(children);
  const validChildren = React.Children.toArray(topLevelChildren).filter(Boolean);
  const content = validChildren.reduce((newChildren, child, index) => {
    const boxID = `Stack-Box-${index}`;
    const item = box ?
    /*#__PURE__*/
    // If wrapped in Box, that Box needs a key.
    // If possible, use an existing content key; use an index-based key only if necessary.
    _createElement(Box, {
      ...boxProps,
      key: child.key || boxID,
      testID: boxID
    }, child) : child;
    if (!index || !space && !divider) return [...newChildren, item];
    const testID = `Stack-${divider ? 'Divider' : 'Spacer'}-${index}`;
    const commonProps = {
      testID,
      space
    };
    const separator = divider ? /*#__PURE__*/_jsx(Divider, {
      vertical: direction.startsWith('row'),
      ...dividerProps,
      ...commonProps
    }, testID) : /*#__PURE__*/_jsx(Spacer, {
      direction: direction.startsWith('row') ? 'row' : 'column',
      ...commonProps
    }, testID);
    return [...newChildren, separator, item];
  }, []);
  return content;
};

/**
 * Unpacks top-level fragments, so that common compositional patterns such as the following examples
 * can be iterated as flat siblings (as if they were `<Child1 /><Child2 /><Child3 />`):
 *
 * - Setting `children` as a property wrapped in a fragment:
 *
 * ```jsx
 * args.children = <><Child1 /><Child2 /><Child3 /></>
 * ```
 *
 * - Defining `children` as a variable wrapped in a fragment:
 *
 * ```jsx
 * const content = <><Child1 /><Child2 /><Child3 /></>
 * if (someCondition) return <SomeWrapper>{content}</SomeWrapper>
 * ```
 *
 * - Using fragments at the top level of a JSX block for conditional rendering:
 *
 * ```jsx
 * <Child1 />
 * {someCondition && (
 *   <>
 *     <Child2 />
 *     <Child3 />
 *   </>
 * )}
 * ```
 *
 * @param {ReactChildren} child
 * @returns {ReactChildren}
 */
const unpackFragment = child => {
  // If this level is a set of top-level siblings rather than one child, check each in turn
  if (React.Children.count(child) > 1) return React.Children.map(child, unpackFragment);

  // When a fragment is found, unpack its children to the top level and check them
  if (child?.type === React.Fragment) return unpackFragment(child.props?.children);

  // Stop unpacking as soon as any non-fragment child is found
  return child;
};
export default getStackedContent;